import React, { useEffect, useState } from 'react';
import {
    Snackbar,
    Box,
    Card,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
    IconButton,
    Chip,
    Avatar,
    Tooltip,
    Divider,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Login as LoginIcon,
    Delete as DeleteIcon,
    Info as InfoIcon,
    Business as BusinessIcon,
    People as PeopleIcon,
    Restaurant as RestaurantIcon,
    CalendarToday as CalendarTodayIcon,
    Storage as StorageIcon,
    Phone as PhoneIcon,
    Person as PersonIcon,
    Language as LanguageIcon,
    AccessTime as AccessTimeIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    WarningAmber as WarningIcon,
    Search as SearchIcon,
    GridView as GridViewIcon,
    ViewList as ViewListIcon,
    OpenInNew as OpenInNewIcon,
    Sync as SyncIcon,
    Domain as DomainIcon,
    AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { Organization, organizationsAPI, authAPI, getImageUrl } from '../../services/api';
import { useAuth as useAuthContext } from '../../contexts/AuthContext';
import { toPersianNumber } from '../../utils/persianNumber';
import PersianDatePicker from '../../components/PersianDatePicker';
import { toJalaali } from 'jalaali-js';
import { alpha } from '@mui/material';

// Dark OLED Palette
const P = {
    bg: '#020617', surface: '#0F172A', surfaceAlt: '#1E293B',
    cta: '#22C55E', text: '#F8FAFC', textDim: '#94A3B8',
    border: 'rgba(248, 250, 252, 0.06)', borderHover: 'rgba(248, 250, 252, 0.12)',
    accent: '#8B5CF6', info: '#3B82F6', warning: '#F59E0B', error: '#EF4444',
};

const AdminOrganizations: React.FC = () => {
    const { user } = useAuthContext();
    const navigate = useNavigate();
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loading, setLoading] = useState(true);
    const [openModal, setOpenModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [detailsOrg, setDetailsOrg] = useState<Organization | null>(null);
    const [deleteConfirmOrg, setDeleteConfirmOrg] = useState<Organization | null>(null);
    const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        domain: '',
        is_active: true,
        expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
    const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');

    useEffect(() => {
        if (user && user.role !== 'superadmin') {
            navigate('/dashboard');
            return;
        }
        loadOrganizations();
    }, [user, navigate]);

    const loadOrganizations = async () => {
        try {
            setLoading(true);
            const data = await organizationsAPI.getOrganizations();
            setOrganizations(data);
        } catch (err: any) {
            setError('خطا در بارگذاری شرکت‌ها و سازمان‌ها');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            await organizationsAPI.createOrganization(formData);
            setSuccess('شرکت جدید با موفقیت اضافه شد');
            setOpenModal(false);
            setFormData({
                name: '',
                slug: '',
                domain: '',
                is_active: true,
                expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
            });
            loadOrganizations();
        } catch (err: any) {
            setError(err.response?.data?.error || 'خطا در اضافه کردن شرکت');
        }
    };

    const handleEdit = (org: Organization) => {
        setEditingOrg(org);
        setFormData({
            name: org.name,
            slug: org.slug || '',
            domain: org.domain || '',
            is_active: org.is_active,
            expires_at: org.expires_at ? new Date(org.expires_at).toISOString().split('T')[0] : '',
        });
        setOpenEditModal(true);
    };

    const handleUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!editingOrg) return;

        try {
            await organizationsAPI.updateOrganization(editingOrg.id, formData);
            setSuccess('مشخصات شرکت با موفقیت به‌روزرسانی شد');
            setOpenEditModal(false);
            setEditingOrg(null);
            setFormData({
                name: '',
                slug: '',
                domain: '',
                is_active: true,
                expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
            });
            loadOrganizations();
        } catch (err: any) {
            setError(err.response?.data?.error || 'خطا در به‌روزرسانی مشخصات شرکت');
        }
    };

    const handleToggleActive = async (orgId: string) => {
        try {
            await organizationsAPI.toggleActive(orgId);
            setSuccess('وضعیت شرکت با موفقیت تغییر کرد');
            loadOrganizations();
        } catch (err: any) {
            setError(err.response?.data?.error || 'خطا در تغییر وضعیت شرکت');
        }
    };

    const handleDelete = async () => {
        if (!deleteConfirmOrg) return;
        try {
            await organizationsAPI.deleteOrganization(deleteConfirmOrg.id);
            setSuccess('شرکت با موفقیت حذف گردید.');
            setDeleteConfirmOrg(null);
            loadOrganizations();
        } catch (err: any) {
            setError(err.response?.data?.error || 'خطا در حذف شرکت');
        }
    };

    const handleImpersonate = async (orgId: string) => {
        try {
            if (!window.confirm('آیا مطمئن هستید که می‌خواهید به عنوان این شرکت وارد سیستم شوید؟')) return;
            await authAPI.impersonate(orgId);
            window.location.href = '/dashboard';
        } catch (err: any) {
            setError(err.response?.data?.error || 'خطا در ورود به پنل شرکت');
        }
    };

    const getRemainingDays = (expiresAt?: string) => {
        if (!expiresAt) return null;
        const diffTime = new Date(expiresAt).getTime() - new Date().getTime();
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    // Calculate Summary Stats
    const totalStaffCount = organizations.reduce((acc, o) => acc + Number(o.active_staff || 0), 0);
    const totalUsersCount = organizations.reduce((acc, o) => acc + Number(o.total_users || 0), 0);
    const totalReservationsCount = organizations.reduce((acc, o) => acc + Number(o.total_reservations || 0), 0);
    const totalMealsCount = organizations.reduce((acc, o) => acc + Number(o.total_meals || 0), 0);

    const formatShamsiDate = (dateStr?: string) => {
        if (!dateStr) return '-';
        try {
            const d = new Date(dateStr);
            const j = toJalaali(d.getFullYear(), d.getMonth() + 1, d.getDate());
            return toPersianNumber(`${j.jy}/${String(j.jm).padStart(2, '0')}/${String(j.jd).padStart(2, '0')}`);
        } catch {
            return '-';
        }
    };

    const filteredOrgs = organizations.filter(org => {
        const query = searchTerm.trim().toLowerCase();
        const matchesSearch =
            !query ||
            org.name.toLowerCase().includes(query) ||
            (org.slug && org.slug.toLowerCase().includes(query)) ||
            (org.domain && org.domain.toLowerCase().includes(query)) ||
            (org.primary_admin_name && org.primary_admin_name.toLowerCase().includes(query)) ||
            (org.primary_admin_mobile && org.primary_admin_mobile.includes(query));

        const matchesStatus =
            statusFilter === 'all' ||
            (statusFilter === 'active' && org.is_active) ||
            (statusFilter === 'inactive' && !org.is_active);

        return matchesSearch && matchesStatus;
    });

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress sx={{ color: P.cta }} />
            </Box>
        );
    }

    return (
        <Box sx={{ pb: 4 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: P.text }}>
                        مدیریت شرکت‌ها و سازمان‌ها
                    </Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, mt: 0.5 }}>
                        مشاهده، پایش وضعیت و مدیریت شرکت‌های تحت پوشش سامانه فودفلو
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setFormData({
                            name: '',
                            slug: '',
                            domain: '',
                            is_active: true,
                            expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                        });
                        setOpenModal(true);
                    }}
                    sx={{
                        fontFamily: 'Vazir, sans-serif',
                        height: '48px',
                        borderRadius: '12px',
                        px: 3,
                        fontWeight: 600,
                        background: `linear-gradient(135deg, ${P.cta} 0%, #16A34A 100%)`,
                        boxShadow: `0 4px 14px ${alpha(P.cta, 0.35)}`,
                        '&:hover': { boxShadow: `0 6px 20px ${alpha(P.cta, 0.4)}` },
                    }}
                >
                    افزودن شرکت جدید
                </Button>
            </Box>

            {/* Overview Metric Cards */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' },
                gap: 2.5,
                mb: 3
            }}>
                <Card sx={{
                    borderRadius: '16px',
                    boxShadow: 'none',
                    p: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    background: P.surface,
                    border: `1px solid ${P.border}`
                }}>
                    <Avatar sx={{ bgcolor: alpha(P.accent, 0.12), color: P.accent, width: 52, height: 52, borderRadius: '14px' }}>
                        <BusinessIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, fontSize: '13px' }}>
                            تعداد شرکت‌ها
                        </Typography>
                        <Typography variant="h5" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: P.text, mt: 0.5 }}>
                            {toPersianNumber(organizations.length.toString())} <span style={{ fontSize: '14px', fontWeight: 500, color: P.textDim }}>سازمان</span>
                        </Typography>
                    </Box>
                </Card>

                <Card sx={{
                    borderRadius: '16px',
                    boxShadow: 'none',
                    p: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    background: P.surface,
                    border: `1px solid ${P.border}`
                }}>
                    <Avatar sx={{ bgcolor: alpha(P.cta, 0.12), color: P.cta, width: 52, height: 52, borderRadius: '14px' }}>
                        <PeopleIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, fontSize: '13px' }}>
                            کل پرسنل فعال
                        </Typography>
                        <Typography variant="h5" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: P.text, mt: 0.5 }}>
                            {toPersianNumber(totalStaffCount.toString())} <span style={{ fontSize: '12px', fontWeight: 400, color: P.textDim }}>نفر (از {toPersianNumber(totalUsersCount.toString())})</span>
                        </Typography>
                    </Box>
                </Card>

                <Card sx={{
                    borderRadius: '16px',
                    boxShadow: 'none',
                    p: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    background: P.surface,
                    border: `1px solid ${P.border}`
                }}>
                    <Avatar sx={{ bgcolor: alpha(P.warning, 0.12), color: P.warning, width: 52, height: 52, borderRadius: '14px' }}>
                        <CalendarTodayIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, fontSize: '13px' }}>
                            کل رزروهای ثبت‌شده
                        </Typography>
                        <Typography variant="h5" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: P.text, mt: 0.5 }}>
                            {toPersianNumber(totalReservationsCount.toString())} <span style={{ fontSize: '14px', fontWeight: 500, color: P.textDim }}>رزرو</span>
                        </Typography>
                    </Box>
                </Card>

                <Card sx={{
                    borderRadius: '16px',
                    boxShadow: 'none',
                    p: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    background: P.surface,
                    border: `1px solid ${P.border}`
                }}>
                    <Avatar sx={{ bgcolor: alpha(P.info, 0.12), color: P.info, width: 52, height: 52, borderRadius: '14px' }}>
                        <RestaurantIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Box>
                        <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, fontSize: '13px' }}>
                            تنوع غذاهای تعریف‌شده
                        </Typography>
                        <Typography variant="h5" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: P.text, mt: 0.5 }}>
                            {toPersianNumber(totalMealsCount.toString())} <span style={{ fontSize: '14px', fontWeight: 500, color: P.textDim }}>آیتم غذایی</span>
                        </Typography>
                    </Box>
                </Card>
            </Box>

            {/* Notification Snackbars */}
            <Snackbar open={!!error} autoHideDuration={3000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => setError('')}>
                    {error}
                </Alert>
            </Snackbar>
            <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="success" sx={{ width: '100%', fontFamily: 'Vazir, sans-serif' }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            </Snackbar>

            {/* Search, Filter & View Controls */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', md: 'center' },
                gap: 2,
                mb: 3
            }}>
                {/* Search Input */}
                <TextField
                    placeholder="جستجو در نام شرکت، اسلاگ، دامنه، نام یا شماره مدیر..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    sx={{
                        flex: { xs: '1 1 auto', md: '1 1 350px' },
                        maxWidth: { md: 450 },
                        '& .MuiOutlinedInput-root': {
                            height: '48px',
                            borderRadius: '12px',
                            bgcolor: P.surface,
                            color: P.text,
                            fontFamily: 'Vazir, sans-serif',
                            border: `1px solid ${P.border}`,
                            '& fieldset': { border: 'none' },
                            '&:hover': { bgcolor: alpha(P.surfaceAlt, 0.6) },
                            '&.Mui-focused': { border: `1px solid ${P.accent}`, bgcolor: P.surface }
                        }
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: P.textDim, ml: 1 }} />
                            </InputAdornment>
                        ),
                    }}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    {/* Status Filter Toggle */}
                    <ToggleButtonGroup
                        value={statusFilter}
                        exclusive
                        onChange={(_, val) => val && setStatusFilter(val)}
                        sx={{
                            height: '48px',
                            bgcolor: P.surface,
                            borderRadius: '12px',
                            border: `1px solid ${P.border}`,
                            p: '4px',
                            gap: '4px',
                            '& .MuiToggleButton-root': {
                                border: 'none',
                                borderRadius: '8px !important',
                                fontFamily: 'Vazir, sans-serif',
                                fontSize: '13px',
                                fontWeight: 600,
                                px: 2,
                                color: P.textDim,
                                '&.Mui-selected': {
                                    bgcolor: alpha(P.accent, 0.2),
                                    color: P.text,
                                    fontWeight: 700,
                                    '&:hover': { bgcolor: alpha(P.accent, 0.28) }
                                }
                            }
                        }}
                    >
                        <ToggleButton value="all">
                            همه ({toPersianNumber(organizations.length.toString())})
                        </ToggleButton>
                        <ToggleButton value="active">
                            فعال ({toPersianNumber(organizations.filter(o => o.is_active).length.toString())})
                        </ToggleButton>
                        <ToggleButton value="inactive">
                            غیرفعال ({toPersianNumber(organizations.filter(o => !o.is_active).length.toString())})
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {/* View Mode Toggle (Cards / Table) */}
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(_, val) => val && setViewMode(val)}
                        sx={{
                            height: '48px',
                            bgcolor: P.surface,
                            borderRadius: '12px',
                            border: `1px solid ${P.border}`,
                            p: '4px',
                            '& .MuiToggleButton-root': {
                                border: 'none',
                                borderRadius: '8px !important',
                                px: 1.5,
                                color: P.textDim,
                                '&.Mui-selected': {
                                    bgcolor: alpha(P.accent, 0.2),
                                    color: P.accent,
                                }
                            }
                        }}
                    >
                        <Tooltip title="نمایش کارتی (پیش‌فرض)" arrow>
                            <ToggleButton value="cards">
                                <GridViewIcon fontSize="small" />
                            </ToggleButton>
                        </Tooltip>
                        <Tooltip title="نمایش جدولی" arrow>
                            <ToggleButton value="table">
                                <ViewListIcon fontSize="small" />
                            </ToggleButton>
                        </Tooltip>
                    </ToggleButtonGroup>
                </Box>
            </Box>

            {/* Organizations Content: Cards or Table */}
            {viewMode === 'cards' ? (
                /* Cards Grid View */
                filteredOrgs.length === 0 ? (
                    <Card sx={{
                        borderRadius: '16px',
                        p: 6,
                        textAlign: 'center',
                        background: P.surface,
                        border: `1px solid ${P.border}`
                    }}>
                        <BusinessIcon sx={{ fontSize: 48, color: P.textDim, mb: 1.5, opacity: 0.5 }} />
                        <Typography variant="h6" sx={{ fontFamily: 'Vazir, sans-serif', color: P.text, fontWeight: 700 }}>
                            هیچ شرکتی یافت نشد
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, mt: 0.5 }}>
                            با تغییر عبارات جستجو یا فیلترها می‌توانید سازمان‌های دیگر را مشاهده نمایید.
                        </Typography>
                    </Card>
                ) : (
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' },
                        gap: 3
                    }}>
                        {filteredOrgs.map((org) => {
                            const isDefaultOrg = org.id === '00000000-0000-0000-0000-000000000000';
                            const remainingDays = getRemainingDays(org.expires_at);
                            const isExpired = remainingDays !== null && remainingDays <= 0;
                            const isNearExpiry = remainingDays !== null && remainingDays > 0 && remainingDays <= 30;

                            return (
                                <Card
                                    key={org.id}
                                    sx={{
                                        borderRadius: '16px',
                                        background: `linear-gradient(180deg, ${P.surface} 0%, #0B132B 100%)`,
                                        border: `1px solid ${P.border}`,
                                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.28)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        transition: 'all 0.25s ease-in-out',
                                        overflow: 'hidden',
                                        position: 'relative',
                                        '&:hover': {
                                            borderColor: alpha(P.accent, 0.45),
                                            transform: 'translateY(-3px)',
                                            boxShadow: `0 12px 32px rgba(0, 0, 0, 0.45), 0 0 16px ${alpha(P.accent, 0.12)}`,
                                        }
                                    }}
                                >
                                    {/* Top Status Gradient Bar */}
                                    <Box sx={{
                                        height: 4,
                                        width: '100%',
                                        background: org.is_active
                                            ? `linear-gradient(90deg, ${P.cta} 0%, ${P.accent} 100%)`
                                            : `linear-gradient(90deg, ${P.error} 0%, #991b1b 100%)`
                                    }} />

                                    {/* Header: Logo, Name, Slug, Status Badge */}
                                    <Box sx={{ p: 2.5, pb: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                                <Avatar
                                                    src={org.logo_url ? getImageUrl(org.logo_url) : undefined}
                                                    sx={{
                                                        width: 52,
                                                        height: 52,
                                                        bgcolor: alpha(P.accent, 0.18),
                                                        color: P.accent,
                                                        borderRadius: '14px',
                                                        fontWeight: 800,
                                                        fontSize: '20px',
                                                        border: `1px solid ${alpha(P.accent, 0.3)}`,
                                                        boxShadow: `0 4px 12px ${alpha(P.accent, 0.25)}`,
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    {org.name.charAt(0)}
                                                </Avatar>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                fontFamily: 'Vazir, sans-serif',
                                                                fontWeight: 800,
                                                                fontSize: '16.5px',
                                                                color: P.text,
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis'
                                                            }}
                                                        >
                                                            {org.name}
                                                        </Typography>
                                                        {isDefaultOrg && (
                                                            <Chip
                                                                label="سازمان اصلی"
                                                                size="small"
                                                                color="primary"
                                                                sx={{ fontFamily: 'Vazir, sans-serif', height: 22, fontSize: '11px', fontWeight: 700 }}
                                                            />
                                                        )}
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontFamily: 'Vazir, sans-serif',
                                                                color: P.textDim,
                                                                bgcolor: alpha(P.surfaceAlt, 0.7),
                                                                px: 1,
                                                                py: 0.2,
                                                                borderRadius: '6px',
                                                                direction: 'ltr',
                                                                fontSize: '11.5px',
                                                                fontWeight: 600
                                                            }}
                                                        >
                                                            slug: {org.slug || 'بدون اسلاگ'}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, fontSize: '11px' }}>
                                                            عضویت: {formatShamsiDate(org.created_at)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>

                                            {/* Status Badge Toggle */}
                                            <Tooltip title={isDefaultOrg ? 'وضعیت سازمان اصلی قابل تغییر نیست' : 'کلیک جهت تغییر وضعیت (فعال / غیرفعال)'} arrow>
                                                <span>
                                                    <Chip
                                                        icon={
                                                            <Box sx={{
                                                                width: 8,
                                                                height: 8,
                                                                borderRadius: '50%',
                                                                bgcolor: org.is_active ? P.cta : P.error,
                                                                boxShadow: `0 0 8px ${org.is_active ? P.cta : P.error}`,
                                                                mr: -0.5
                                                            }} />
                                                        }
                                                        label={org.is_active ? 'فعال' : 'غیرفعال'}
                                                        size="small"
                                                        onClick={() => !isDefaultOrg && handleToggleActive(org.id)}
                                                        sx={{
                                                            fontFamily: 'Vazir, sans-serif',
                                                            fontWeight: 700,
                                                            fontSize: '12px',
                                                            height: 28,
                                                            px: 0.5,
                                                            cursor: isDefaultOrg ? 'default' : 'pointer',
                                                            bgcolor: org.is_active ? alpha(P.cta, 0.12) : alpha(P.error, 0.12),
                                                            color: org.is_active ? P.cta : P.error,
                                                            border: `1px solid ${org.is_active ? alpha(P.cta, 0.3) : alpha(P.error, 0.3)}`,
                                                            '&:hover': isDefaultOrg ? {} : {
                                                                bgcolor: org.is_active ? alpha(P.cta, 0.22) : alpha(P.error, 0.22)
                                                            }
                                                        }}
                                                    />
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </Box>

                                    {/* Domain & Admin Quick Info Box */}
                                    <Box sx={{ px: 2.5, pb: 2 }}>
                                        <Box sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                                            gap: 1.5,
                                            bgcolor: alpha(P.surfaceAlt, 0.5),
                                            p: 1.5,
                                            borderRadius: '12px',
                                            border: `1px solid ${P.border}`
                                        }}>
                                            {/* Domain */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                                <LanguageIcon sx={{ fontSize: 18, color: P.info, flexShrink: 0 }} />
                                                <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                                                    <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, display: 'block', fontSize: '11px' }}>
                                                        دامنه اختصاصی:
                                                    </Typography>
                                                    {org.domain ? (
                                                        <a
                                                            href={`https://${org.domain}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                color: P.info,
                                                                textDecoration: 'none',
                                                                direction: 'ltr',
                                                                fontWeight: 600,
                                                                fontSize: '12.5px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '4px'
                                                            }}
                                                        >
                                                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{org.domain}</span>
                                                            <OpenInNewIcon sx={{ fontSize: 13 }} />
                                                        </a>
                                                    ) : (
                                                        <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, fontWeight: 500 }}>
                                                            دامنه پیش‌فرض فودفلو
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>

                                            {/* Admin */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                                <PersonIcon sx={{ fontSize: 18, color: P.accent, flexShrink: 0 }} />
                                                <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                                                    <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, display: 'block', fontSize: '11px' }}>
                                                        مدیر اصلی:
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                                                        <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600, fontSize: '12.5px', color: P.text, whiteSpace: 'nowrap' }}>
                                                            {org.primary_admin_name || 'ثبت‌نشده'}
                                                        </Typography>
                                                        {org.primary_admin_mobile && (
                                                            <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, direction: 'ltr', fontSize: '11px' }}>
                                                                ({toPersianNumber(org.primary_admin_mobile)})
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Operational Metrics: 4-Box Grid */}
                                    <Box sx={{ px: 2.5, pb: 2 }}>
                                        <Box sx={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(2, 1fr)',
                                            gap: 1.5,
                                        }}>
                                            {/* Personnel Tile */}
                                            <Box sx={{
                                                p: 1.5,
                                                borderRadius: '12px',
                                                bgcolor: alpha(P.cta, 0.05),
                                                border: `1px solid ${alpha(P.cta, 0.12)}`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.2
                                            }}>
                                                <Avatar sx={{ bgcolor: alpha(P.cta, 0.15), color: P.cta, width: 38, height: 38, borderRadius: '10px' }}>
                                                    <PeopleIcon sx={{ fontSize: 20 }} />
                                                </Avatar>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, fontSize: '11px', display: 'block' }}>
                                                        پرسنل فعال
                                                    </Typography>
                                                    <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, fontSize: '14.5px', color: P.text }}>
                                                        {toPersianNumber((org.active_staff || 0).toString())} <span style={{ fontSize: '11px', fontWeight: 400, color: P.textDim }}>نفر</span>
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, fontSize: '10.5px' }}>
                                                        از کل {toPersianNumber((org.total_users || 0).toString())} کاربر
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* Meals Tile */}
                                            <Box sx={{
                                                p: 1.5,
                                                borderRadius: '12px',
                                                bgcolor: alpha(P.info, 0.05),
                                                border: `1px solid ${alpha(P.info, 0.12)}`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.2
                                            }}>
                                                <Avatar sx={{ bgcolor: alpha(P.info, 0.15), color: P.info, width: 38, height: 38, borderRadius: '10px' }}>
                                                    <RestaurantIcon sx={{ fontSize: 20 }} />
                                                </Avatar>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, fontSize: '11px', display: 'block' }}>
                                                        تنوع منوی غذا
                                                    </Typography>
                                                    <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, fontSize: '14.5px', color: P.text }}>
                                                        {toPersianNumber((org.total_meals || 0).toString())} <span style={{ fontSize: '11px', fontWeight: 400, color: P.textDim }}>آیتم</span>
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, fontSize: '10.5px' }}>
                                                        منوی فعال رستوران
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* Reservations Tile */}
                                            <Box sx={{
                                                p: 1.5,
                                                borderRadius: '12px',
                                                bgcolor: alpha(P.warning, 0.05),
                                                border: `1px solid ${alpha(P.warning, 0.12)}`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.2
                                            }}>
                                                <Avatar sx={{ bgcolor: alpha(P.warning, 0.15), color: P.warning, width: 38, height: 38, borderRadius: '10px' }}>
                                                    <CalendarTodayIcon sx={{ fontSize: 20 }} />
                                                </Avatar>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, fontSize: '11px', display: 'block' }}>
                                                        مجموع رزروها
                                                    </Typography>
                                                    <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, fontSize: '14.5px', color: P.text }}>
                                                        {toPersianNumber((org.total_reservations || 0).toString())} <span style={{ fontSize: '11px', fontWeight: 400, color: P.textDim }}>رزرو</span>
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, fontSize: '10.5px' }}>
                                                        سفارشات ثبت‌شده
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* Sub-companies Tile */}
                                            <Box sx={{
                                                p: 1.5,
                                                borderRadius: '12px',
                                                bgcolor: alpha(P.accent, 0.05),
                                                border: `1px solid ${alpha(P.accent, 0.12)}`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.2
                                            }}>
                                                <Avatar sx={{ bgcolor: alpha(P.accent, 0.15), color: P.accent, width: 38, height: 38, borderRadius: '10px' }}>
                                                    <BusinessIcon sx={{ fontSize: 20 }} />
                                                </Avatar>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, fontSize: '11px', display: 'block' }}>
                                                        شرکت‌های تابعه
                                                    </Typography>
                                                    <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, fontSize: '14.5px', color: P.text }}>
                                                        {toPersianNumber((org.total_companies || 0).toString())} <span style={{ fontSize: '11px', fontWeight: 400, color: P.textDim }}>واحد</span>
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, fontSize: '10.5px' }}>
                                                        زیرمجموعه سازمانی
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Sync & Expiry Footer Ribbon */}
                                    <Box sx={{ px: 2.5, pb: 2, mt: 'auto' }}>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            flexWrap: 'wrap',
                                            gap: 1,
                                            p: 1.5,
                                            borderRadius: '12px',
                                            bgcolor: alpha(P.surfaceAlt, 0.35),
                                            border: `1px solid ${P.border}`
                                        }}>
                                            {/* Database Sync Info */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <StorageIcon sx={{ fontSize: 16, color: org.sync_db_type ? P.info : P.textDim }} />
                                                {org.sync_db_type ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                                        <Chip
                                                            label={org.sync_db_type.toUpperCase()}
                                                            size="small"
                                                            sx={{
                                                                fontFamily: 'Vazir, sans-serif',
                                                                height: 20,
                                                                fontSize: '10.5px',
                                                                fontWeight: 700,
                                                                bgcolor: alpha(P.info, 0.15),
                                                                color: P.info,
                                                            }}
                                                        />
                                                        <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', fontSize: '11px', color: org.sync_cron_enabled ? P.cta : P.textDim }}>
                                                            {org.sync_cron_enabled ? 'همگام‌سازی شبانه' : 'همگام دستی'}
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, fontSize: '11px' }}>
                                                        اتصال دیتابیس ثبت‌نشده
                                                    </Typography>
                                                )}
                                            </Box>

                                            {/* Expiration Info */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <AccessTimeIcon sx={{ fontSize: 15, color: P.textDim }} />
                                                {org.expires_at ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                                        <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, fontSize: '11px' }}>
                                                            {formatShamsiDate(org.expires_at)}
                                                        </Typography>
                                                        {remainingDays !== null && (
                                                            <Chip
                                                                label={
                                                                    isExpired
                                                                        ? 'منقضی شده'
                                                                        : `${toPersianNumber(remainingDays.toString())} روز مانده`
                                                                }
                                                                size="small"
                                                                color={isExpired ? 'error' : (isNearExpiry ? 'warning' : 'success')}
                                                                sx={{ fontFamily: 'Vazir, sans-serif', height: 20, fontSize: '10.5px', fontWeight: 700 }}
                                                            />
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, fontSize: '11px' }}>
                                                        نامحدود
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>

                                    <Divider sx={{ borderColor: P.border }} />

                                    {/* Bottom Action Buttons */}
                                    <Box sx={{
                                        p: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1.5,
                                        bgcolor: alpha(P.surfaceAlt, 0.25)
                                    }}>
                                        <Button
                                            variant="contained"
                                            startIcon={<LoginIcon />}
                                            onClick={() => handleImpersonate(org.id)}
                                            sx={{
                                                flex: 1,
                                                height: '44px',
                                                borderRadius: '12px',
                                                fontFamily: 'Vazir, sans-serif',
                                                fontWeight: 700,
                                                fontSize: '13px',
                                                background: `linear-gradient(135deg, ${P.accent} 0%, #6D28D9 100%)`,
                                                boxShadow: `0 4px 12px ${alpha(P.accent, 0.3)}`,
                                                '&:hover': {
                                                    background: `linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)`,
                                                    boxShadow: `0 6px 16px ${alpha(P.accent, 0.45)}`,
                                                }
                                            }}
                                        >
                                            ورود به پنل
                                        </Button>

                                        <Tooltip title="مشاهده شناسنامه و آمار کامل شرکت" arrow>
                                            <Button
                                                variant="outlined"
                                                onClick={() => setDetailsOrg(org)}
                                                startIcon={<InfoIcon />}
                                                sx={{
                                                    height: '44px',
                                                    borderRadius: '12px',
                                                    fontFamily: 'Vazir, sans-serif',
                                                    fontWeight: 600,
                                                    fontSize: '12.5px',
                                                    color: P.info,
                                                    borderColor: alpha(P.info, 0.4),
                                                    '&:hover': {
                                                        borderColor: P.info,
                                                        bgcolor: alpha(P.info, 0.1)
                                                    }
                                                }}
                                            >
                                                شناسنامه
                                            </Button>
                                        </Tooltip>

                                        <Tooltip title="ویرایش اطلاعات شرکت" arrow>
                                            <IconButton
                                                onClick={() => handleEdit(org)}
                                                sx={{
                                                    height: 44,
                                                    width: 44,
                                                    borderRadius: '12px',
                                                    bgcolor: alpha(P.cta, 0.1),
                                                    color: P.cta,
                                                    border: `1px solid ${alpha(P.cta, 0.2)}`,
                                                    '&:hover': {
                                                        bgcolor: alpha(P.cta, 0.2),
                                                        borderColor: P.cta
                                                    }
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title={isDefaultOrg ? 'سازمان اصلی غیرقابل حذف است' : 'حذف سازمان'} arrow>
                                            <span>
                                                <IconButton
                                                    disabled={isDefaultOrg}
                                                    onClick={() => setDeleteConfirmOrg(org)}
                                                    sx={{
                                                        height: 44,
                                                        width: 44,
                                                        borderRadius: '12px',
                                                        bgcolor: alpha(P.error, 0.1),
                                                        color: P.error,
                                                        border: `1px solid ${alpha(P.error, 0.2)}`,
                                                        '&:hover': {
                                                            bgcolor: alpha(P.error, 0.2),
                                                            borderColor: P.error
                                                        },
                                                        '&.Mui-disabled': {
                                                            bgcolor: alpha(P.textDim, 0.05),
                                                            color: alpha(P.textDim, 0.3),
                                                            border: `1px solid ${alpha(P.textDim, 0.1)}`
                                                        }
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Box>
                                </Card>
                            );
                        })}
                    </Box>
                )
            ) : (
                /* Table View */
                <Card sx={{ borderRadius: '16px', boxShadow: 'none', overflow: 'hidden', background: P.surface, border: `1px solid ${P.border}` }}>
                    <TableContainer>
                        <Table>
                            <TableHead sx={{ bgcolor: alpha(P.surfaceAlt, 0.5) }}>
                                <TableRow>
                                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, width: 60, color: P.textDim, borderBottom: `1px solid ${P.border}` }}>ردیف</TableCell>
                                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: P.textDim, borderBottom: `1px solid ${P.border}` }}>نام شرکت / سازمان</TableCell>
                                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: P.textDim, borderBottom: `1px solid ${P.border}` }}>دامنه اختصاصی</TableCell>
                                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: P.textDim, borderBottom: `1px solid ${P.border}` }}>مدیر ارشد</TableCell>
                                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: P.textDim, borderBottom: `1px solid ${P.border}` }}>پرسنل فعال / کل</TableCell>
                                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: P.textDim, borderBottom: `1px solid ${P.border}` }}>آمار غذا و رزرو</TableCell>
                                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: P.textDim, borderBottom: `1px solid ${P.border}` }}>دیتابیس همگام‌سازی</TableCell>
                                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: P.textDim, borderBottom: `1px solid ${P.border}` }}>اعتبار اشتراک</TableCell>
                                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: P.textDim, borderBottom: `1px solid ${P.border}` }}>وضعیت</TableCell>
                                    <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, textAlign: 'center', color: P.textDim, borderBottom: `1px solid ${P.border}` }}>عملیات</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredOrgs.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                                            <Typography variant="body1" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim }}>
                                                هیچ شرکتی با معیارهای جستجو یافت نشد
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredOrgs.map((org, index) => {
                                        const isDefaultOrg = org.id === '00000000-0000-0000-0000-000000000000';
                                        const remainingDays = getRemainingDays(org.expires_at);

                                        return (
                                            <TableRow key={org.id} hover sx={{ '&:hover': { bgcolor: alpha(P.text, 0.02) } }}>
                                                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600, color: P.text, borderBottom: `1px solid ${P.border}` }}>
                                                    {toPersianNumber((index + 1).toString())}
                                                </TableCell>

                                                {/* Name & Logo */}
                                                <TableCell sx={{ borderBottom: `1px solid ${P.border}` }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                        <Avatar
                                                            src={org.logo_url ? getImageUrl(org.logo_url) : undefined}
                                                            sx={{
                                                                width: 44,
                                                                height: 44,
                                                                bgcolor: alpha(P.accent, 0.15),
                                                                color: P.accent,
                                                                borderRadius: '12px',
                                                                fontWeight: 700,
                                                                boxShadow: `0 2px 8px ${alpha(P.accent, 0.2)}`
                                                            }}
                                                        >
                                                            {org.name.charAt(0)}
                                                        </Avatar>
                                                        <Box>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, fontSize: '14.5px', color: P.text }}>
                                                                    {org.name}
                                                                </Typography>
                                                                {isDefaultOrg && (
                                                                    <Chip
                                                                        label="سازمان اصلی"
                                                                        size="small"
                                                                        color="primary"
                                                                        sx={{ fontFamily: 'Vazir, sans-serif', height: 20, fontSize: '11px', fontWeight: 600 }}
                                                                    />
                                                                )}
                                                            </Box>
                                                            <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, direction: 'ltr', display: 'inline-block' }}>
                                                                شناسه: {org.slug || 'بدون اسلاگ'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </TableCell>

                                                {/* Domain */}
                                                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', borderBottom: `1px solid ${P.border}` }}>
                                                    {org.domain ? (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <LanguageIcon sx={{ fontSize: 16, color: P.info }} />
                                                            <a
                                                                href={`https://${org.domain}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ color: P.info, textDecoration: 'none', direction: 'ltr', fontWeight: 600 }}
                                                            >
                                                                {org.domain}
                                                            </a>
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim }}>
                                                            دامنه پیش‌فرض (فودفلو)
                                                        </Typography>
                                                    )}
                                                </TableCell>

                                                {/* Admin info */}
                                                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', borderBottom: `1px solid ${P.border}` }}>
                                                    <Box>
                                                        <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600, fontSize: '13.5px', color: P.text }}>
                                                            {org.primary_admin_name || 'ادمین اصلی'}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, direction: 'ltr', display: 'block' }}>
                                                            {org.primary_admin_mobile ? toPersianNumber(org.primary_admin_mobile) : '-'}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>

                                                {/* Users Count */}
                                                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', borderBottom: `1px solid ${P.border}` }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                        <Chip
                                                            icon={<PeopleIcon sx={{ fontSize: '16px !important' }} />}
                                                            label={`${toPersianNumber((org.active_staff || 0).toString())} پرسنل فعال`}
                                                            size="small"
                                                            color="success"
                                                            variant="outlined"
                                                            sx={{ fontFamily: 'Vazir, sans-serif', height: 26, fontSize: '12px', fontWeight: 600 }}
                                                        />
                                                        <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim, px: 0.5 }}>
                                                            مجموع کل کاربران: {toPersianNumber((org.total_users || 0).toString())}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>

                                                {/* Food & Reservation stats */}
                                                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', borderBottom: `1px solid ${P.border}` }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                                        <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif', fontSize: '13px', color: P.text }}>
                                                            🍱 {toPersianNumber((org.total_meals || 0).toString())} آیتم غذا
                                                        </Typography>
                                                        <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif', fontSize: '13px', color: P.text }}>
                                                            📑 {toPersianNumber((org.total_reservations || 0).toString())} رزرو
                                                        </Typography>
                                                        {Boolean(org.total_companies) && (
                                                            <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim }}>
                                                                🏢 {toPersianNumber((org.total_companies || 0).toString())} زیرشرکت
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>

                                                {/* Sync status */}
                                                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', borderBottom: `1px solid ${P.border}` }}>
                                                    {org.sync_db_type ? (
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                            <Chip
                                                                icon={<StorageIcon sx={{ fontSize: '14px !important' }} />}
                                                                label={org.sync_db_type.toUpperCase()}
                                                                size="small"
                                                                color="info"
                                                                sx={{ fontFamily: 'Vazir, sans-serif', height: 24, fontSize: '11px', fontWeight: 600 }}
                                                            />
                                                            <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: org.sync_cron_enabled ? '#16a34a' : '#94a3b8' }}>
                                                                {org.sync_cron_enabled ? 'همگام‌سازی شبانه فعال' : 'همگام‌سازی دستی'}
                                                            </Typography>
                                                        </Box>
                                                    ) : (
                                                        <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim }}>
                                                            اتصال ثبت‌نشده
                                                        </Typography>
                                                    )}
                                                </TableCell>

                                                {/* Expiry */}
                                                <TableCell sx={{ fontFamily: 'Vazir, sans-serif', borderBottom: `1px solid ${P.border}` }}>
                                                    {org.expires_at ? (() => {
                                                        const date = new Date(org.expires_at);
                                                        const j = toJalaali(date.getFullYear(), date.getMonth() + 1, date.getDate());
                                                        const isExpired = remainingDays !== null && remainingDays <= 0;
                                                        const isNearExpiry = remainingDays !== null && remainingDays > 0 && remainingDays <= 30;

                                                        return (
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                <Typography variant="body2" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600, fontSize: '13px', color: P.text }}>
                                                                    {toPersianNumber(`${j.jy}/${j.jm}/${j.jd}`)}
                                                                </Typography>
                                                                {remainingDays !== null && (
                                                                    <Chip
                                                                        label={
                                                                            isExpired
                                                                                ? 'منقضی شده'
                                                                                : `${toPersianNumber(remainingDays.toString())} روز مانده`
                                                                        }
                                                                        size="small"
                                                                        color={isExpired ? 'error' : (isNearExpiry ? 'warning' : 'success')}
                                                                        sx={{ fontFamily: 'Vazir, sans-serif', height: 22, fontSize: '11px', fontWeight: 600 }}
                                                                    />
                                                                )}
                                                            </Box>
                                                        );
                                                    })() : '-'}
                                                </TableCell>

                                                {/* Status */}
                                                <TableCell sx={{ borderBottom: `1px solid ${P.border}` }}>
                                                    <Tooltip title="کلیک جهت فعال/غیرفعال‌سازی" arrow>
                                                        <Chip
                                                            label={org.is_active ? 'فعال' : 'غیرفعال'}
                                                            color={org.is_active ? 'success' : 'error'}
                                                            size="small"
                                                            onClick={() => !isDefaultOrg && handleToggleActive(org.id)}
                                                            sx={{
                                                                fontFamily: 'Vazir, sans-serif',
                                                                cursor: isDefaultOrg ? 'default' : 'pointer',
                                                                fontWeight: 600,
                                                                '&:hover': isDefaultOrg ? {} : { opacity: 0.8 }
                                                            }}
                                                        />
                                                    </Tooltip>
                                                </TableCell>

                                                {/* Actions */}
                                                <TableCell align="center" sx={{ borderBottom: `1px solid ${P.border}` }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                        <Tooltip title="مشاهده کامل جزئیات شرکت" arrow>
                                                            <IconButton
                                                                color="info"
                                                                size="small"
                                                                onClick={() => setDetailsOrg(org)}
                                                                sx={{ bgcolor: alpha(P.info, 0.1), color: P.info, '&:hover': { bgcolor: alpha(P.info, 0.2) } }}
                                                            >
                                                                <InfoIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>

                                                        <Tooltip title="ورود به سیستم به عنوان این شرکت" arrow>
                                                            <IconButton
                                                                color="secondary"
                                                                size="small"
                                                                onClick={() => handleImpersonate(org.id)}
                                                                sx={{ bgcolor: alpha(P.accent, 0.1), color: P.accent, '&:hover': { bgcolor: alpha(P.accent, 0.2) } }}
                                                            >
                                                                <LoginIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>

                                                        <Tooltip title="ویرایش اطلاعات" arrow>
                                                            <IconButton
                                                                color="primary"
                                                                size="small"
                                                                onClick={() => handleEdit(org)}
                                                                sx={{ bgcolor: alpha(P.cta, 0.1), color: P.cta, '&:hover': { bgcolor: alpha(P.cta, 0.2) } }}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>

                                                        <Tooltip title={isDefaultOrg ? 'سازمان اصلی غیرقابل حذف است' : 'حذف سازمان'} arrow>
                                                            <span>
                                                                <IconButton
                                                                    color="error"
                                                                    size="small"
                                                                    disabled={isDefaultOrg}
                                                                    onClick={() => setDeleteConfirmOrg(org)}
                                                                    sx={{ bgcolor: alpha(P.error, 0.1), '&:hover': { bgcolor: alpha(P.error, 0.2) } }}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            )}

            {/* Organization Details Modal */}
            <Dialog
                open={!!detailsOrg}
                onClose={() => setDetailsOrg(null)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px', background: P.surface, border: `1px solid ${P.border}`, color: P.text } }}
            >
                {detailsOrg && (
                    <>
                        <DialogTitle sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, pb: 1, borderBottom: `1px solid ${P.border}`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar
                                src={detailsOrg.logo_url ? getImageUrl(detailsOrg.logo_url) : undefined}
                                sx={{ width: 40, height: 40, bgcolor: alpha(P.accent, 0.15), color: P.accent, borderRadius: '10px' }}
                            >
                                {detailsOrg.name.charAt(0)}
                            </Avatar>
                            <Box>
                                <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, fontSize: '17px' }}>
                                    شناسنامه تفصیلی {detailsOrg.name}
                                </Typography>
                                <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim }}>
                                    شناسه سیستمی: {detailsOrg.id}
                                </Typography>
                            </Box>
                        </DialogTitle>
                        <DialogContent sx={{ pt: 3 }}>
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                gap: 2.5,
                                mb: 2.5
                            }}>
                                {/* General Information */}
                                <Card sx={{ p: 2.5, borderRadius: '12px', bgcolor: P.surfaceAlt, border: `1px solid ${P.border}` }}>
                                    <Typography variant="subtitle1" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: P.text, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <BusinessIcon sx={{ color: P.info }} fontSize="small" /> مشخصات عمومی و دامنه
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim }}>نام سازمان:</Typography>
                                            <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>{detailsOrg.name}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim }}>اسلاگ آدرس:</Typography>
                                            <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }} dir="ltr">{detailsOrg.slug || '-'}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim }}>دامنه اختصاصی:</Typography>
                                            <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }} dir="ltr">{detailsOrg.domain || 'پیش‌فرض'}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontFamily: 'Vazir, sans-serif', color: '#64748b' }}>وضعیت دسترسی:</Typography>
                                            <Chip
                                                label={detailsOrg.is_active ? 'فعال و مجاز' : 'غیرفعال'}
                                                color={detailsOrg.is_active ? 'success' : 'error'}
                                                size="small"
                                                sx={{ fontFamily: 'Vazir, sans-serif', height: 22, fontWeight: 600 }}
                                            />
                                        </Box>
                                    </Box>
                                </Card>

                                {/* Admin & Contact */}
                                <Card sx={{ p: 2.5, borderRadius: '12px', bgcolor: P.surfaceAlt, border: `1px solid ${P.border}` }}>
                                    <Typography variant="subtitle1" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: P.text, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PersonIcon sx={{ color: P.accent }} fontSize="small" /> مدیریت و اطلاعات تماس
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim }}>مدیر اصلی سازمان:</Typography>
                                            <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>{detailsOrg.primary_admin_name || 'ثبت‌نشده'}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim }}>شماره تماس مدیر:</Typography>
                                            <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }} dir="ltr">
                                                {detailsOrg.primary_admin_mobile ? toPersianNumber(detailsOrg.primary_admin_mobile) : '-'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim }}>تعداد مدیران سیستم:</Typography>
                                            <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                                                {toPersianNumber(detailsOrg.total_admins || 0)} نفر
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim }}>اتصال دیتابیس همگام‌سازی:</Typography>
                                            <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 600 }}>
                                                {detailsOrg.sync_db_type ? detailsOrg.sync_db_type.toUpperCase() : 'تنظیم نشده'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Card>
                            </Box>

                            {/* Operational & Personnel Stats */}
                            <Card sx={{ p: 2.5, borderRadius: '12px', bgcolor: alpha(P.cta, 0.06), border: `1px solid ${alpha(P.cta, 0.12)}` }}>
                                <Typography variant="subtitle1" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: P.cta, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <StorageIcon sx={{ color: P.cta }} fontSize="small" /> آمار پرسنلی و عملیاتی این شرکت
                                </Typography>
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                                    gap: 2
                                }}>
                                    <Box>
                                        <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim }}>پرسنل فعال:</Typography>
                                        <Typography variant="h6" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: P.text }}>
                                            {toPersianNumber(detailsOrg.active_staff || 0)} نفر
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim }}>کل کاربران ثبت‌شده:</Typography>
                                        <Typography variant="h6" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: P.text }}>
                                            {toPersianNumber(detailsOrg.total_users || 0)} نفر
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim }}>غذاهای تعریف‌شده:</Typography>
                                        <Typography variant="h6" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: P.text }}>
                                            {toPersianNumber(detailsOrg.total_meals || 0)} آیتم
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: P.textDim }}>رزروهای ثبت‌شده:</Typography>
                                        <Typography variant="h6" sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: P.text }}>
                                            {toPersianNumber(detailsOrg.total_reservations || 0)} رزرو
                                        </Typography>
                                    </Box>
                                </Box>
                            </Card>
                        </DialogContent>
                        <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${P.border}` }}>
                            <Button
                                variant="contained"
                                onClick={() => setDetailsOrg(null)}
                                sx={{ fontFamily: 'Vazir, sans-serif', borderRadius: '12px', height: '44px', px: 3 }}
                            >
                                بستن
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={!!deleteConfirmOrg}
                onClose={() => setDeleteConfirmOrg(null)}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px', background: P.surface, border: `1px solid ${P.border}`, color: P.text } }}
            >
                <DialogTitle sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700, color: P.error }}>
                    حذف شرکت / سازمان
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily: 'Vazir, sans-serif', fontSize: '14.5px', color: P.text }}>
                        آیا از حذف شرکت <strong>«{deleteConfirmOrg?.name}»</strong> اطمینان کامل دارید؟
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: 'Vazir, sans-serif', color: '#dc2626', display: 'block', mt: 1 }}>
                        ⚠️ با انجام این عملیات کلیه داده‌های مرتبط با این شرکت حذف خواهند شد.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDeleteConfirmOrg(null)} sx={{ fontFamily: 'Vazir, sans-serif' }}>
                        انصراف
                    </Button>
                    <Button onClick={handleDelete} color="error" variant="contained" sx={{ fontFamily: 'Vazir, sans-serif', borderRadius: '10px' }}>
                        تأیید و حذف
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Modal */}
            <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px', background: P.surface, border: `1px solid ${P.border}`, color: P.text } }}>
                <DialogTitle sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700 }}>افزودن شرکت جدید</DialogTitle>
                <Box component="form" onSubmit={handleSubmit}>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            fullWidth
                            label="نام شرکت / سازمان"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                            InputLabelProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
                            inputProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
                        />
                        <TextField
                            fullWidth
                            label="اسلاگ (شناسه یکتا انگلیسی)"
                            value={formData.slug}
                            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                            InputLabelProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
                            inputProps={{ sx: { fontFamily: 'Vazir, sans-serif', dir: 'ltr' } }}
                            placeholder="e.g. delijan"
                        />
                        <TextField
                            fullWidth
                            label="دامنه اختصاصی (اختیاری)"
                            value={formData.domain}
                            onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                            InputLabelProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
                            inputProps={{ sx: { fontFamily: 'Vazir, sans-serif', dir: 'ltr' } }}
                            placeholder="e.g. food.company.com"
                        />
                        <Box>
                            <PersianDatePicker
                                label="تاریخ انقضای اشتراک"
                                value={formData.expires_at}
                                onChange={(date) => setFormData({ ...formData, expires_at: date })}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, pt: 0 }}>
                        <Button onClick={() => setOpenModal(false)} sx={{ fontFamily: 'Vazir, sans-serif' }}>
                            انصراف
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            sx={{ fontFamily: 'Vazir, sans-serif', borderRadius: '12px', height: '48px', px: 3 }}
                        >
                            ذخیره و ایجاد
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>

            {/* Edit Modal */}
            <Dialog open={openEditModal} onClose={() => setOpenEditModal(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px', background: P.surface, border: `1px solid ${P.border}`, color: P.text } }}>
                <DialogTitle sx={{ fontFamily: 'Vazir, sans-serif', fontWeight: 700 }}>ویرایش مشخصات شرکت</DialogTitle>
                <Box component="form" onSubmit={handleUpdate}>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            fullWidth
                            label="نام شرکت / سازمان"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                            InputLabelProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
                            inputProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
                        />
                        <TextField
                            fullWidth
                            label="اسلاگ (شناسه یکتا انگلیسی)"
                            value={formData.slug}
                            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                            InputLabelProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
                            inputProps={{ sx: { fontFamily: 'Vazir, sans-serif', dir: 'ltr' } }}
                        />
                        <TextField
                            fullWidth
                            label="دامنه اختصاصی (اختیاری)"
                            value={formData.domain}
                            onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                            InputLabelProps={{ sx: { fontFamily: 'Vazir, sans-serif' } }}
                            inputProps={{ sx: { fontFamily: 'Vazir, sans-serif', dir: 'ltr' } }}
                        />
                        <Box>
                            <PersianDatePicker
                                label="تاریخ انقضای اشتراک"
                                value={formData.expires_at}
                                onChange={(date) => setFormData({ ...formData, expires_at: date })}
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, pt: 0 }}>
                        <Button onClick={() => setOpenEditModal(false)} sx={{ fontFamily: 'Vazir, sans-serif' }}>
                            انصراف
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            sx={{ fontFamily: 'Vazir, sans-serif', borderRadius: '12px', height: '48px', px: 3 }}
                        >
                            بروزرسانی
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
        </Box>
    );
};

export default AdminOrganizations;
